import React, {
    FC,
    forwardRef,
    memo,
    ReactNode,
    RefAttributes,
    useImperativeHandle,
    useRef,
    useCallback,
    useLayoutEffect,
} from "react";
import { Container, Header, Content } from "./styles";

interface CoreLayoutWithProperties extends FC<CoreLayoutProps & RefAttributes<HTMLDivElement>> {
    Header: React.FunctionComponent<CoreLayoutHeaderProps>;
    Content: React.FunctionComponent<CoreLayoutContentProps & RefAttributes<HTMLDivElement>>;
}

export interface CoreLayoutProps {
    children: ReactNode;
    customClassName?: string;
    isJioHotstarFlow?: boolean;
    isCartError?: boolean;
    "data-jiohotstar"?: string;
}

export interface CoreLayoutContentProps {
    children: ReactNode;
    className?: string;
    contentId?: string;
    isDineoutCart?: boolean;
    isJioHotstarFlow?: boolean;
    onScrollContainerReady?: (el: HTMLElement | null) => void;
    hideScrollbar?: boolean;
}

export interface CoreLayoutHeaderProps {
    children: ReactNode;
    className?: string;
    isCart?: boolean;
    isPopCart?: boolean;
    isHeaderExtensionExists?: boolean;
    isPageScrolled?: boolean;
    isIncognitoCart?: boolean;
    isAddressSelected?: boolean;
    isUserBlackMember?: boolean;
    isThemeChange?: boolean;
    isBolt?: boolean;
    isOtg?: boolean;
    isExpress?: boolean;
    isLottieAnimation?: boolean;
    dataTestId?: string;
    isDineoutCartHeader?: boolean;
    isBillPaymentCartHeader?: boolean;
    is99StoreLottie?: boolean;
    isEOCartLottie?: boolean;
    isGiftingCart?: boolean;
    headerGradient?: string;
    headerShadow?: string;
    isJioHotstarFlow?: boolean;
}

const CoreLayout: CoreLayoutWithProperties = function CoreLayout(props: CoreLayoutProps): React.ReactElement {
    return (
        <Container
            ux4={true}
            className={props.customClassName}
            isJioHotstarFlow={props.isJioHotstarFlow}
            isCartError={props.isCartError}
            data-jiohotstar={props["data-jiohotstar"]}
        >
            {props.children}
        </Container>
    );
} as CoreLayoutWithProperties;

CoreLayout.Header = memo(function CoreLayoutContent(props: CoreLayoutHeaderProps): React.ReactElement {
    // TODO: revamp below header height logic to make it more dynamic instead of predefined

    return (
        <Header
            data-testid={props.dataTestId}
            className={props.className}
            ux4={!props.isIncognitoCart || !props.isCart}
            isCart={props.isCart}
            isPopCart={props.isPopCart}
            isHeaderExtensionExists={props.isHeaderExtensionExists}
            isPageScrolled={props.isPageScrolled}
            isIncognitoCart={props.isIncognitoCart}
            isAddressSelected={props.isAddressSelected}
            isUserBlackMember={props.isUserBlackMember}
            isThemeChange={props.isThemeChange}
            isBolt={props.isBolt}
            isOtg={props.isOtg}
            isExpress={props.isExpress}
            isDineoutCartHeader={props.isDineoutCartHeader}
            isBillPaymentCartHeader={props.isBillPaymentCartHeader}
            is99StoreLottie={props.is99StoreLottie}
            isEOCartLottie={props.isEOCartLottie}
            isGiftingCart={props.isGiftingCart}
            headerGradient={props.headerGradient}
            headerShadow={props.headerShadow}
            isJioHotstarFlow={props.isJioHotstarFlow}
        >
            {props.children}
        </Header>
    );
});

CoreLayout.Content = forwardRef<HTMLDivElement, CoreLayoutContentProps>(function CoreLayoutContent(
    props: CoreLayoutContentProps,
    ref,
): React.ReactElement {
    const divRef = useRef<HTMLDivElement | null>(null);
    const { onScrollContainerReady } = props;

    const setRef = useCallback(
        (el: HTMLDivElement | null) => {
            divRef.current = el;
            onScrollContainerReady?.(el);
            if (typeof ref === "function") ref(el);
            else if (ref) ref.current = el;
        },
        [onScrollContainerReady, ref],
    );

    useLayoutEffect(() => {
        onScrollContainerReady?.(divRef.current);
        return () => onScrollContainerReady?.(null);
    }, [onScrollContainerReady]);

    useImperativeHandle(ref, () => ({
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        scrollTo: (x: number, y: number): void => {
            divRef.current?.scrollTo(x, y);
        },
        getScrollPosition: (): number => {
            return divRef?.current?.scrollTop ?? 0;
        },
        addEventListener: (eventName: string, fn: EventListenerOrEventListenerObject, options?: boolean): void => {
            return divRef.current?.addEventListener(eventName, fn, options);
        },
        removeEventListener: (eventName: string, fn: EventListenerOrEventListenerObject, options?: boolean): void => {
            return divRef.current?.removeEventListener(eventName, fn, options);
        },
    }));

    return (
        <Content
            isDineoutCart={props.isDineoutCart}
            id={props.contentId}
            ref={setRef}
            className={props.className}
            ux4={true}
            isJioHotstarFlow={props.isJioHotstarFlow}
            hideScrollbar={props.hideScrollbar}
        >
            {props.children}
        </Content>
    );
});

if (__DEV__) CoreLayout.Content.displayName = "CoreLayoutContent";

export default CoreLayout;
